/**
 * CalendarSetManager
 *
 * Dialog for managing calendar sets (named visibility presets).
 * Supports creating, renaming, deleting, and editing calendar membership.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Pencil, Trash2, Plus } from 'lucide-react';
import type { CalendarCollection, CalendarSet } from '../types';

interface CalendarSetManagerProps {
  open: boolean;
  onClose: () => void;
  calendarSets: CalendarSet[];
  calendars: CalendarCollection[];
  onCreateSet: (name: string, calendarIds: string[]) => void;
  onRenameSet: (id: string, name: string) => void;
  onDeleteSet: (id: string) => void;
  onUpdateSetCalendars: (id: string, calendarIds: string[]) => void;
}

export function CalendarSetManager({
  open,
  onClose,
  calendarSets,
  calendars,
  onCreateSet,
  onRenameSet,
  onDeleteSet,
  onUpdateSetCalendars,
}: CalendarSetManagerProps) {
  const [newSetName, setNewSetName] = useState('');
  const [newSetCalendarIds, setNewSetCalendarIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleCreate = () => {
    if (!newSetName.trim()) return;
    onCreateSet(newSetName.trim(), newSetCalendarIds);
    setNewSetName('');
    setNewSetCalendarIds([]);
  };

  const handleStartRename = (set: CalendarSet) => {
    setEditingId(set.id);
    setEditingName(set.name);
  };

  const handleFinishRename = () => {
    if (editingId && editingName.trim()) {
      onRenameSet(editingId, editingName.trim());
    }
    setEditingId(null);
    setEditingName('');
  };

  const toggleCalendarInNewSet = (calId: string) => {
    setNewSetCalendarIds((prev) =>
      prev.includes(calId) ? prev.filter((id) => id !== calId) : [...prev, calId],
    );
  };

  const toggleCalendarInSet = (setId: string, calId: string) => {
    const set = calendarSets.find((s) => s.id === setId);
    if (!set) return;
    const newIds = set.calendarIds.includes(calId)
      ? set.calendarIds.filter((id) => id !== calId)
      : [...set.calendarIds, calId];
    onUpdateSetCalendars(setId, newIds);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Calendar Sets</DialogTitle>
          <DialogDescription>
            Create named presets to quickly switch which calendars are visible.
          </DialogDescription>
        </DialogHeader>

        {/* Existing sets */}
        <div className="space-y-3">
          {calendarSets.map((set) => (
            <div key={set.id} className="border rounded-md p-3 space-y-2">
              <div className="flex items-center gap-2">
                {editingId === set.id ? (
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleFinishRename}
                    onKeyDown={(e) => e.key === 'Enter' && handleFinishRename()}
                    className="h-7 text-sm flex-1"
                    autoFocus
                  />
                ) : (
                  <span className="text-sm font-medium flex-1">{set.name}</span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleStartRename(set)}
                  title="Rename"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive"
                  onClick={() => onDeleteSet(set.id)}
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <div className="space-y-1">
                {calendars.map((cal) => (
                  <label key={cal.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox
                      checked={set.calendarIds.includes(cal.id)}
                      onCheckedChange={() => toggleCalendarInSet(set.id, cal.id)}
                      className="h-3 w-3"
                    />
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ backgroundColor: cal.color }}
                    />
                    <span>{cal.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {calendarSets.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-3">
              No calendar sets yet. Create one below.
            </p>
          )}
        </div>

        {/* Create new set */}
        <div className="border-t pt-3 space-y-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            New Set
          </span>
          <Input
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            placeholder="Set name"
            className="text-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <div className="space-y-1">
            {calendars.map((cal) => (
              <label key={cal.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={newSetCalendarIds.includes(cal.id)}
                  onCheckedChange={() => toggleCalendarInNewSet(cal.id)}
                  className="h-3 w-3"
                />
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: cal.color }}
                />
                <span>{cal.name}</span>
              </label>
            ))}
          </div>
          <Button size="sm" onClick={handleCreate} disabled={!newSetName.trim()} className="w-full">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Create Set
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
